import "@progress/kendo-theme-default/dist/all.css";
import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Editor,
  EditorChangeEvent,
  EditorMountEvent,
  EditorPasteEvent,
  EditorTools,
  ProseMirror
} from "@progress/kendo-react-editor";
import { CustomForeColor } from "./components/custom-fore-color";
import { CustomBackColor } from "./components/custom-back-color";
import { CustomFormatBlock } from "./components/custom-format-block";
import { CustomStationery } from "./components/custom-stationery";
import { CustomEmojiPicker } from "./components/custom-emoji-picker";
import { CustomSignatureButton } from "./components/custom-signature-button";
import {
  KendoEditorGlobalStyles,
  StyledKendoEditor
} from "./kendo-editor.styles";
import {
  handleEditorError,
  useAvailableSpaceHeight,
  useColorPickerEvents,
  useEditorFocus
} from "./hooks";
import { type PluginManagerOptions, usePluginManager } from "./plugins";
import { createKeyboardShortcutsPlugin } from "./plugins";
import { StationeryOptions } from "@coxauto-ui/cxm/communications/api";
import CustomFontName from "./components/custom-font-name";
import { createMediaWidgetNodeView } from "./editor-widgets/media-widget-node-view";

const { EditorView, Schema } = ProseMirror;

export interface KendoEditorProps {
  /** The HTML content value of the editor */
  value: string;
  /** Callback function called when the editor content changes */
  onChange: (htmlContent: string) => void;
  /** Whether the editor is disabled and non-interactive */
  isDisabled?: boolean;
  /** Test ID for testing purposes */
  "data-testid"?: string;
  /** Additional CSS class name for custom styling */
  className?: string;
  /** Stationery options including items and default ID */
  stationeryOptions: StationeryOptions;
  /** External dependencies that should trigger editor height recalculation (e.g., when CC/BCC fields are toggled) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  recalculateDependencies?: any[];
  /** Optional external ref to access the Kendo Editor instance (e.g., for programmatic content insertion) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editorRef?: RefObject<any>;
  /** Called when the user edits a media widget title inline. Receives the endlink URL, media ID, and new title. */
  onMediaTitleChange?: (
    endlinkUrl: string,
    mediaId: string,
    newTitle: string
  ) => void;
}

// Extract Editor Tools for better organization
const {
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignRight,
  AlignCenter,
  AlignJustify,
  OrderedList,
  UnorderedList,
  Undo,
  Redo,
  Link,
  Unlink,
  CleanFormatting,
  ViewHtml
} = EditorTools;

// Define the toolbar configuration - will be created dynamically to support props
export const createEditorToolbarConfig = (
  stationeryOptions: StationeryOptions
) => {
  return [
    () =>
      stationeryOptions.stationeries?.length > 0 && [
        <CustomStationery
          key="stationery"
          stationeryOptions={stationeryOptions}
        />
      ],
    [Undo, Redo],
    [CustomFontName, CustomFormatBlock],
    [CustomForeColor, CustomBackColor],
    [Bold, Italic, Underline],
    [AlignLeft, AlignCenter, AlignRight, AlignJustify],
    [OrderedList, UnorderedList],
    [Link, Unlink],
    [CustomSignatureButton, CustomEmojiPicker],
    [CleanFormatting],
    [ViewHtml]
  ];
};

// eslint-disable-next-line react/prop-types
const KendoEditorComponent = React.memo<KendoEditorProps>(
  ({
    value,
    onChange,
    isDisabled = false,
    "data-testid": testId,
    className,
    stationeryOptions,
    recalculateDependencies = [],
    editorRef: externalEditorRef,
    onMediaTitleChange
  }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const internalRef = useRef<any>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editorRef: RefObject<any> = externalEditorRef ?? internalRef;
    const [editorContent, setEditorContent] = useState<string>(value);
    // Ref mirrors editorContent so handleEditorMount can read the latest value
    // without being in its dependency array (avoids NodeView re-creation on every
    // keystroke, which caused browsers to re-fetch img src URLs).
    const editorContentRef = useRef<string>(value);
    editorContentRef.current = editorContent;
    // Ref keeps the callback fresh without adding it to handleEditorMount's deps,
    // mirroring the editorContentRef pattern above.
    const onMediaTitleChangeRef = useRef(onMediaTitleChange);
    onMediaTitleChangeRef.current = onMediaTitleChange;
    // Create toolbar configuration with stationery options
    const editorToolbarConfig = useMemo(() => {
      return createEditorToolbarConfig(stationeryOptions);
    }, [stationeryOptions]);

    // Use available space height hook to dynamically calculate editor height
    const { editorHeight, recalculateHeight } = useAvailableSpaceHeight(
      editorRef,
      {
        minHeight: 200,
        bottomMargin: 100,
        recalculateDependencies
      }
    );

    // Initialize plugin manager with both autolink and ordered list styling enabled
    const pluginManagerOptions = useMemo<PluginManagerOptions>(
      () => ({
        autolink: {
          enabled: true
        },
        orderedListStyle: {
          enabled: true
        }
      }),
      []
    );

    const pluginManager = usePluginManager(pluginManagerOptions);

    // Create keyboard plugins for Tab/Shift-Tab indent/outdent shortcuts
    const keyboardPlugins = useMemo(() => {
      return createKeyboardShortcutsPlugin();
    }, []);

    // Memoize editor focus options
    const editorFocusOptions = useMemo(
      () => ({
        retryAttempts: 3,
        retryDelay: 100
      }),
      []
    );

    const { focusEditor } = useEditorFocus(
      editorRef,
      isDisabled,
      editorFocusOptions
    );

    useColorPickerEvents(editorRef, isDisabled, focusEditor);

    // Sync internal state with external value prop
    useEffect(() => {
      setEditorContent(value);
    }, [value]);

    /**
     * Memoized change handler to prevent unnecessary re-renders
     * Only recreates when dependencies actually change
     */
    const handleEditorChange = useCallback(
      (event: EditorChangeEvent) => {
        // Prevent changes when disabled
        if (isDisabled) return;
        try {
          setEditorContent(event.html);
          onChange(event.html);
        } catch (error) {
          handleEditorError(error as Error, "editor content change fallback");
        }
      },
      [isDisabled, onChange]
    );

    /**
     * Enhanced mount handler with simplified plugin transaction monitoring
     */
    const handleEditorMount = useCallback(
      (event: EditorMountEvent) => {
        const { viewProps } = event;
        const { state, view } = viewProps;
        const { schema } = state;

        // updateSchema mutates existing node specs (paragraph, image) in-place via
        // object reference, and returns the new OrderedMap from addToEnd (immutable).
        // We compile a fresh Schema from that map so mediaWidget gets a real NodeType
        // with atom: true / selectable: true — spec-only entries have no effect.
        const nodesWithWidget = updateSchema(schema);
        const newSchema = new Schema({
          nodes: nodesWithWidget,
          marks: schema.spec.marks
        });

        // Create a streamlined plugin to monitor plugin transactions and sync to React
        const reactSyncPlugin = new ProseMirror.Plugin({
          key: new ProseMirror.PluginKey("reactSync"),

          appendTransaction(transactions, _oldState, _newState) {
            // Check if any transaction was generated by our plugins
            const hasPluginTransaction = transactions.some(tr => {
              return (
                tr.getMeta("autolink-applied") ||
                tr.getMeta("orderedListStyle-applied")
              );
            });

            if (hasPluginTransaction) {
              // Streamlined content sync with fallback methods
              const syncContent = () => {
                try {
                  let updatedHtml = "";

                  // Try ProseMirror DOMSerializer first (most accurate)
                  if (_newState?.doc && _newState?.schema) {
                    const { DOMSerializer } = ProseMirror;
                    if (DOMSerializer?.fromSchema) {
                      const serializer = DOMSerializer.fromSchema(
                        _newState.schema
                      );
                      const fragment = serializer.serializeFragment(
                        _newState.doc.content
                      );
                      const tempDiv = document.createElement("div");
                      tempDiv.appendChild(fragment);
                      updatedHtml = tempDiv.innerHTML;
                    }
                  }

                  // Fallback to Kendo Editor methods
                  if (!updatedHtml && editorRef.current?.getHtml) {
                    updatedHtml = editorRef.current.getHtml();
                  } else if (!updatedHtml && editorRef.current?.value) {
                    updatedHtml = editorRef.current.value;
                  } else if (!updatedHtml && view?.dom?.innerHTML) {
                    updatedHtml = view.dom.innerHTML;
                  }

                  // Update React state if content changed
                  if (updatedHtml && updatedHtml !== editorContentRef.current) {
                    const safeContent = String(updatedHtml || "");

                    setEditorContent(safeContent);
                    onChange(safeContent);
                    return true;
                  }
                  return false;
                } catch (error) {
                  handleEditorError(error as Error, "plugin content sync");
                  return false;
                }
              };

              // Try immediate sync, with fallback delays if needed
              if (!syncContent()) {
                setTimeout(() => syncContent(), 50);
              }
            }

            return null; // Don't create additional transactions
          }
        });

        // Get plugins from plugin manager
        const autolinkPlugins = pluginManager.getPlugins();

        // Combine keyboard shortcuts with plugins including our React sync plugin
        const plugins = [
          ...state.plugins,
          keyboardPlugins,
          ...autolinkPlugins,
          reactSyncPlugin // Add our sync plugin LAST to monitor other plugins
        ];

        // Build a new EditorState against newSchema so NodeType objects (paragraph,
        // image, mediaWidget, etc.) all reference the newly-compiled schema.
        //
        // Re-parse from the raw HTML value (editorContent) rather than from the old
        // ProseMirror doc. On remount (e.g. after a tab switch) the initial schema
        // doesn't include mediaWidget, so ProseMirror descends into <span data-media-widget>
        // and strips the attribute. Parsing the raw HTML through newSchema preserves it.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { DOMParser: PMParser, EditorState: PMEditorState } =
          ProseMirror as any;
        let newDoc;
        try {
          if (PMParser?.fromSchema && editorContentRef.current) {
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = editorContentRef.current;
            newDoc = PMParser.fromSchema(newSchema).parse(tempDiv);
          }
        } catch {
          // fall through to default empty doc
        }
        if (!newDoc) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          newDoc = (newSchema.topNodeType as any).createAndFill();
        }

        const newState = PMEditorState.create({ doc: newDoc, plugins });

        const mediaWidgetNodeView = createMediaWidgetNodeView({
          onMediaTitleChangeRef
        });

        const newView = new EditorView(
          { mount: event.dom },
          {
            ...event.viewProps,
            state: newState,
            nodeViews: { mediaWidget: mediaWidgetNodeView }
          }
        );

        // Set the editor view reference for plugin interactions
        pluginManager.setEditorView(newView);

        return newView;
      },
      [pluginManager, onChange, keyboardPlugins]
    );

    /**
     * Schema manipulation for custom paragraph styling and email template
     * attribute preservation.
     *
     * Email templates use HTML attributes (width, height, cellpadding,
     * cellspacing, border, align, valign) that ProseMirror's default schema
     * does not include. Without these attrs in the spec, ProseMirror silently
     * drops them during parse, causing visual differences between the editor
     * and the actual email output.
     *
     * The existing parseDOM handlers already capture all DOM attributes via
     * getAttrs: domAttributes, and toDOM outputs all non-null attrs via
     * pmAttributes — so registering the attrs here is sufficient.
     *
     * For documentation on extending the schema further, see:
     * ./docs/SchemaManipulation.md
     */
    const updateSchema = (schema: InstanceType<typeof Schema>) => {
      // --- Paragraph: default margin ---
      const paragraph = { ...schema.spec.nodes.get("paragraph") };
      paragraph.attrs = paragraph.attrs || {};
      paragraph.attrs["style"] = { default: "margin: 0px" };
      schema.spec.nodes.update("paragraph", paragraph);

      // --- Table: email layout attributes ---
      const table = { ...schema.spec.nodes.get("table") };
      table.attrs = {
        ...table.attrs,
        width: { default: null },
        height: { default: null },
        cellpadding: { default: null },
        cellspacing: { default: null },
        border: { default: null },
        align: { default: null }
      };
      schema.spec.nodes.update("table", table);

      // --- Table row: height and alignment ---
      const tableRow = { ...schema.spec.nodes.get("table_row") };
      tableRow.attrs = {
        ...tableRow.attrs,
        height: { default: null },
        align: { default: null },
        valign: { default: null }
      };
      schema.spec.nodes.update("table_row", tableRow);

      // --- Table cell: dimensions and alignment ---
      const tableCell = { ...schema.spec.nodes.get("table_cell") };
      tableCell.attrs = {
        ...tableCell.attrs,
        width: { default: null },
        height: { default: null },
        align: { default: null },
        valign: { default: null }
      };
      schema.spec.nodes.update("table_cell", tableCell);

      // --- Table header: same as table cell ---
      const tableHeader = { ...schema.spec.nodes.get("table_header") };
      tableHeader.attrs = {
        ...tableHeader.attrs,
        width: { default: null },
        height: { default: null },
        align: { default: null },
        valign: { default: null }
      };
      schema.spec.nodes.update("table_header", tableHeader);

      // Preserve data-media-endlink on image nodes.
      // ProseMirror's default image schema strips unknown data attributes — extending
      // parseDOM and toDOM here ensures the attribute survives editor round-trips so
      // wrapMediaWidget can read it at send time.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const image: any = schema.spec.nodes.get("image");
      if (image) {
        // 1. Register the attrs so ProseMirror tracks them on the node
        image.attrs = {
          ...(image.attrs || {}),
          "data-media-endlink": { default: null },
          "data-media-title": { default: null }
        };

        // 2. Extend each parseDOM rule to also read the attribute from the DOM element
        if (Array.isArray(image.parseDOM)) {
          image.parseDOM = image.parseDOM.map(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (rule: any) => {
              const originalGetAttrs = rule.getAttrs;
              return {
                ...rule,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                getAttrs: (dom: any) => {
                  const base = originalGetAttrs ? originalGetAttrs(dom) : {};
                  if (base === false) return false;
                  return {
                    ...(base as object),
                    "data-media-endlink":
                      dom?.getAttribute?.("data-media-endlink") ?? null,
                    "data-media-title":
                      dom?.getAttribute?.("data-media-title") ?? null
                  };
                }
              };
            }
          );
        }

        // 3. Extend toDOM to serialize the attribute back to the DOM element
        const originalToDOM = image.toDOM;
        if (originalToDOM) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          image.toDOM = (node: any) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const result: [string, Record<string, any>, ...any[]] =
              originalToDOM(node);
            const endlink = node.attrs?.["data-media-endlink"];
            const mediaTitle = node.attrs?.["data-media-title"];
            if (endlink) {
              result[1] = { ...result[1], "data-media-endlink": endlink };
            }
            if (mediaTitle) {
              result[1] = { ...result[1], "data-media-title": mediaTitle };
            }
            return result;
          };
        }

        schema.spec.nodes.update("image", image);
      }

      // Register mediaWidget as an atomic inline node so div[data-media-widget]
      // survives ProseMirror round-trips without being parsed into its children.
      //
      // atom: true     — single-unit; one backspace/delete removes the whole widget
      // selectable:    — click selects the whole node, not a cursor position inside it
      // inline + group — lives inside paragraphs alongside text
      //
      // outerHTML is stored in the 'html' attr so toDOM can reconstruct the full widget
      // HTML verbatim after each round-trip, preserving thumbnails, badge, and endlink URL.
      const mediaWidgetSpec = {
        attrs: {
          "data-media-widget": { default: null },
          html: { default: "" }
        },
        atom: true,
        selectable: true,
        inline: true,
        group: "inline",
        parseDOM: [
          {
            // span is inline — ProseMirror's DOMParser only applies inline-NodeType
            // rules to inline HTML elements. div (block) is never matched against
            // inline rules, causing ProseMirror to descend into the children instead.
            // Note: ProseMirror tag selectors support [attr] syntax but NOT .className.
            // priority 100 > default 50 — ensures this node rule is checked before
            // Kendo's inline-style mark rules for <span>, which are added to the
            // DOMParser rule set first and would otherwise consume our widget span.
            tag: "span[data-media-widget]",
            priority: 100,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            getAttrs: (dom: any) => ({
              "data-media-widget": dom.getAttribute("data-media-widget"),
              html: dom.outerHTML
            })
          }
        ],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        toDOM: (node: any) => {
          const temp = document.createElement("span");
          temp.innerHTML = node.attrs.html || "";
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return (
            (temp.firstElementChild as any) ?? [
              "span",
              { "data-media-widget": node.attrs["data-media-widget"] }
            ]
          );
        }
      };
      // addToEnd is immutable — it returns a NEW OrderedMap with mediaWidget appended.
      // The original schema.spec.nodes is NOT mutated, so we must capture and return
      // the new map so handleEditorMount can compile a Schema that includes mediaWidget.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (schema.spec.nodes as any).addToEnd(
        "mediaWidget",
        mediaWidgetSpec
      );
    };

    /**
     * Memoized paste handler for better performance
     */
    const handlePasteHtml = useCallback(
      (event: EditorPasteEvent): string | void => {
        // Prevent pasting when disabled
        if (isDisabled) return "";
        return event.pastedHtml;
      },
      [isDisabled]
    );

    const styledEditorProps = useMemo(
      () => ({
        $isDisabled: isDisabled,
        className,
        "data-testid": testId
      }),
      [isDisabled, className, testId]
    );

    // Apply dynamic height to editor content element
    useEffect(() => {
      const applyDynamicHeight = () => {
        try {
          const editorContentElement =
            editorRef.current?.querySelector?.(".k-editor-content") ||
            document.querySelector(".k-editor-content");

          if (editorContentElement instanceof HTMLElement) {
            editorContentElement.style.height = `${editorHeight}px`;
            editorContentElement.style.maxHeight = `${editorHeight}px`;
          }

          // Also apply to any inner content div elements that Kendo might create
          const innerContentElements = editorRef.current?.querySelectorAll?.(
            ".k-editor-content div[style*='height']"
          );
          if (innerContentElements) {
            innerContentElements.forEach((element: HTMLElement) => {
              if (element instanceof HTMLElement) {
                element.style.height = "100%";
                element.style.maxHeight = "100%";
              }
            });
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn("Error applying dynamic height:", error);
        }
      };

      // Apply height immediately and after a small delay to ensure editor is rendered
      applyDynamicHeight();
      const timeoutId = setTimeout(applyDynamicHeight, 100);

      return () => clearTimeout(timeoutId);
    }, [editorHeight, editorRef]);

    // Toggle horizontal scroll when content (templates, images) overflows
    useEffect(() => {
      const editorContentEl =
        editorRef.current?.querySelector?.(".k-editor-content") ||
        document.querySelector(".k-editor-content");

      if (!(editorContentEl instanceof HTMLElement)) return;

      const proseMirrorEl = editorContentEl.querySelector(".ProseMirror");
      if (!(proseMirrorEl instanceof HTMLElement)) return;

      editorContentEl.style.overflowX =
        proseMirrorEl.scrollWidth > editorContentEl.clientWidth
          ? "auto"
          : "hidden";
    }, [editorContent, editorRef]);

    // Trigger height recalculation when disabled state changes
    useEffect(() => {
      if (recalculateHeight) {
        recalculateHeight();
      }
    }, [isDisabled, recalculateHeight]);

    return (
      <>
        <KendoEditorGlobalStyles />
        <StyledKendoEditor {...styledEditorProps}>
          <Editor
            defaultEditMode="div"
            ref={editorRef}
            tools={editorToolbarConfig}
            value={editorContent}
            onChange={handleEditorChange}
            onMount={handleEditorMount}
            onPasteHtml={handlePasteHtml}
          />
        </StyledKendoEditor>
      </>
    );
  }
);

KendoEditorComponent.displayName = "KendoEditor";

export default KendoEditorComponent;
